import { ActiveSearchTags } from '@/components/ActiveSearchTags';
import { CollapsableSearch } from '@/components/CollapsableSearch';
import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import type {
  AdminInvoiceReportsSearchFormData,
  AdminInvoiceReportsSearchQuery,
} from '@/features/admin_invoice_reports/types';
import { useShowSearchValue } from '@/hooks/invoice_reports/useShowSearchValue';
import { useTranslation } from '@/i18n';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { SearchForm } from './components/SearchForm';
import { useAdminInvoiceReportsFieldOrders } from './hooks/useAdminInvoiceReportsFieldOrders';
import { useConfigForm } from './hooks/useConfigForm';
import {
  convertFormDataToApiQuery,
  getDefaultFormValues,
  useSearchForm,
} from './hooks/useSearchForm';
import styles from './Search.module.scss';

const cx = classnames.bind(styles);

interface SearchProps {
  defaultValues?: AdminInvoiceReportsSearchFormData;
  onSearch: (data: AdminInvoiceReportsSearchQuery) => void;
  searchQuery?: AdminInvoiceReportsSearchQuery;
  isLoading?: boolean;
}

export const InnerSearch: FC<SearchProps> = memo(
  ({ defaultValues, onSearch, searchQuery, isLoading }) => {
    const { t } = useTranslation();
    const form = useForm<AdminInvoiceReportsSearchFormData>({
      defaultValues: defaultValues ?? getDefaultFormValues(),
      mode: 'all',
    });

    const { handleReset } = useSearchForm({ form });
    const {
      labels,
      selectProps,
      inputProps,
      statusOptions,
      paymentStatusOptions,
      mfFileOptions,
      paymentMethodOptions,
      deptsExisted,
      reportDeptsSetting,
    } = useConfigForm();
    const { FIELD_ORDERS } = useAdminInvoiceReportsFieldOrders();

    const onHandleSearch = useCallback(
      (data: AdminInvoiceReportsSearchFormData) => {
        onSearch(convertFormDataToApiQuery(data));
      },
      [onSearch]
    );

    const { searchValuesShow } = useShowSearchValue({
      searchQuery,
      fieldOrders: FIELD_ORDERS,
      payeeOptions: [],
      excludeFields: [],
      customTransformers: {},
      alwaysShowFields: [],
    });

    const renderSearchForm = useCallback(
      () => (
        <SearchForm
          form={form}
          onSubmit={onHandleSearch}
          onReset={handleReset}
          isLoading={isLoading}
          labels={labels}
          selectProps={selectProps}
          inputProps={inputProps}
          statusOptions={statusOptions}
          paymentStatusOptions={paymentStatusOptions}
          mfFileOptions={mfFileOptions}
          paymentMethodOptions={paymentMethodOptions}
          deptsExisted={deptsExisted}
          reportDeptsSetting={reportDeptsSetting}
        />
      ),
      [
        form,
        onHandleSearch,
        handleReset,
        isLoading,
        labels,
        selectProps,
        inputProps,
        statusOptions,
        paymentStatusOptions,
        mfFileOptions,
        paymentMethodOptions,
        deptsExisted,
        reportDeptsSetting,
      ]
    );

    return (
      <div className={cx('search-container')}>
        <div className={cx('header')}>
          <CollapsableSearch
            initialOpen
            openTitle={t('admin_invoice_reports.search.collapse_title')}
            closeTitle={t('admin_invoice_reports.search.expand_title')}
            sectionTitleExpandedClassName={cx('section-title-expanded')}
          >
            {renderSearchForm}
          </CollapsableSearch>
        </div>
        <ActiveSearchTags activeSearchValues={searchValuesShow} />
      </div>
    );
  }
);
InnerSearch.displayName = 'InnerSearch';

const renderErrorFallback = () => null;

export const Search: FC<SearchProps> = memo(
  ({ defaultValues, onSearch, searchQuery, isLoading }) => (
    <Suspense
      fallback={<SuspenseLoading className={cx('search-container-loading')} />}
    >
      <ErrorBoundary fallback={renderErrorFallback}>
        <InnerSearch
          defaultValues={defaultValues}
          onSearch={onSearch}
          searchQuery={searchQuery}
          isLoading={isLoading}
        />
      </ErrorBoundary>
    </Suspense>
  )
);
Search.displayName = 'Search';
